import React, { useEffect, useMemo } from 'react';
import { Routes, Route, useParams } from 'react-router-dom';
import useLocalStorage from '../../shared/hooks/useLocalStorage';
import { autoMergeContent } from '../../shared/engine/contentMerger';
import { getComparisonSets, CONTENT_VERSION } from './data/comparisonSets';
import MatrixHome from './components/MatrixHome';
import MatrixView from './components/MatrixView';
import RapidFire from './components/RapidFire';
import FillTheBlank from './components/FillTheBlank';
import FullRecall from './components/FullRecall';

/**
 * Protocol Comparison Matrix root.
 * Manages comparison data and drill stats.
 */
export default function MatrixApp() {
  const [comparisons, setComparisons] = useLocalStorage('matrix', 'comparisons', []);
  const [drillStats, setDrillStats] = useLocalStorage('matrix', 'drill-stats', {});

  // Auto-merge: seeds on first load AND adds new sets from code updates.
  // pruneRemoved drops matrices that have been retired from the seed (e.g.
  // wireless topics removed from the ENCOR v1.1 blueprint). After pruning
  // comparisons, drop orphaned drill-stats entries for the same IDs so the
  // Home screen accuracy chips don't point at matrices that no longer exist.
  useEffect(() => {
    const seed = getComparisonSets();
    autoMergeContent({
      namespace: 'matrix',
      key: 'comparisons',
      seedData: seed,
      seedVersion: CONTENT_VERSION,
      currentData: comparisons,
      setData: setComparisons,
      updateFields: ['attributes', 'title', 'protocols'],
      pruneRemoved: true,
    });

    const liveIds = new Set(seed.map(c => c.id));
    const customIds = new Set(comparisons.filter(c => c.is_custom).map(c => c.id));
    const orphanStatKeys = Object.keys(drillStats).filter(
      id => !liveIds.has(id) && !customIds.has(id)
    );
    if (orphanStatKeys.length > 0) {
      const next = { ...drillStats };
      for (const id of orphanStatKeys) delete next[id];
      setDrillStats(next);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Routes>
      <Route path="/" element={<MatrixHome comparisons={comparisons} drillStats={drillStats} />} />
      <Route path="/:id" element={<MatrixViewRoute comparisons={comparisons} />} />
      <Route path="/:id/drill/rapid" element={<DrillRoute comparisons={comparisons} type="rapid" />} />
      <Route path="/:id/drill/fill" element={<DrillRoute comparisons={comparisons} type="fill" />} />
      <Route path="/:id/drill/recall" element={<DrillRoute comparisons={comparisons} type="recall" />} />
    </Routes>
  );
}

function MatrixViewRoute({ comparisons }) {
  const { id } = useParams();
  const matrix = useMemo(() => comparisons.find(c => c.id === id), [comparisons, id]);
  return <MatrixView matrix={matrix} />;
}

function DrillRoute({ comparisons, type }) {
  const { id } = useParams();
  const matrix = useMemo(() => comparisons.find(c => c.id === id), [comparisons, id]);

  if (!matrix) {
    return <div className="p-8 text-center text-gray-500">Matrix not found.</div>;
  }

  switch (type) {
    case 'fill':
      return <FillTheBlank matrix={matrix} />;
    case 'recall':
      return <FullRecall matrix={matrix} />;
    case 'rapid':
    default:
      return <RapidFire matrix={matrix} />;
  }
}
